import { useState, useMemo, useEffect, useRef } from 'react'
import { Player } from '../types'
import { usePlayerStore, useAuthStore } from '../stores'
import { apiClient } from '../utils/api'
import PlayerCard from '../components/PlayerCard'
import PlayerDetail from '../components/PlayerDetail'
import PlayerSearch from '../components/PlayerSearch'
import { Award, Mail, Twitter, LogIn, ArrowUpDown, ChevronDown, ChevronLeft, ChevronRight, Star, Shield, Target, Crosshair } from 'lucide-react'

type SortField = 'name' | 'age' | 'goals' | 'assists' | 'recent' | 'passes' | 'potential' | 'position' | 'rating'
type SortOrder = 'asc' | 'desc'

interface FeaturedSlot {
  label: string
  icon: React.ReactNode
  color: string
  bg: string
  positions: string[]
}

const FEATURED_SLOTS: FeaturedSlot[] = [
  { label: 'Featured Goalkeeper', icon: <Shield size={20} />, color: 'text-yellow-400', bg: 'bg-yellow-500/10', positions: ['GK'] },
  { label: 'Featured Defender', icon: <Shield size={20} />, color: 'text-blue-400', bg: 'bg-blue-500/10', positions: ['CB', 'LB', 'RB', 'RWB', 'LWB'] },
  { label: 'Featured Midfielder', icon: <Crosshair size={20} />, color: 'text-green-400', bg: 'bg-green-500/10', positions: ['CDM', 'CM', 'CAM'] },
  { label: 'Featured Attacker', icon: <Target size={20} />, color: 'text-red-400', bg: 'bg-red-500/10', positions: ['LW', 'RW', 'SS', 'CF'] },
]

export default function TopTalentsPage({ onLoginClick }: { onLoginClick: () => void }) {
  const { players, clubs } = usePlayerStore()
  const { user } = useAuthStore()
  const [selectedPlayer, setSelectedPlayer] = useState<string | null>(null)
  const [playerRatings, setPlayerRatings] = useState<Record<string, number>>({})
  const carouselRef = useRef<HTMLDivElement>(null)
  const [canScrollLeft, setCanScrollLeft] = useState(false)
  const [canScrollRight, setCanScrollRight] = useState(true)

  // Search and Filter state
  const [searchResults, setSearchResults] = useState(players)
  const [showSortMenu, setShowSortMenu] = useState(false)
  const [sortField, setSortField] = useState<SortField>('rating')
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc')

  const isGuest = !user
  const { visibleClubIds, hiddenClubNames } = useMemo(() => {
    const clubArray = Array.isArray(clubs) ? clubs : []
    const visible = new Set(
      clubArray
        .filter((club) => club.is_visible !== false)
        .map((club) => club.id)
    )
    const hidden = new Set(
      clubArray
        .filter((club) => club.is_visible === false)
        .map((club) => club.name?.trim().toLowerCase())
        .filter(Boolean)
    )
    return { visibleClubIds: visible, hiddenClubNames: hidden }
  }, [clubs])

  // Fetch average ratings for all players
  useEffect(() => {
    const fetchRatings = async () => {
      const ratingsMap: Record<string, number> = {}
      // Fetch ratings in batches
      const playerIds = players.map(p => p.id).filter(Boolean)
      for (let i = 0; i < playerIds.length; i += 50) {
        const batch = playerIds.slice(i, i + 50)
        const results = await Promise.all(batch.map(id => apiClient.getAverageRating(id)))
        batch.forEach((id, idx) => {
          if (results[idx] > 0) ratingsMap[id] = results[idx]
        })
      }
      setPlayerRatings(ratingsMap)
    }
    fetchRatings()
  }, [players])

  // Featured players: highest rated in each position group
  const featuredPlayers = useMemo(() => {
    return FEATURED_SLOTS.map(slot => {
      const candidates = players.filter(p =>
        p.main_position && slot.positions.includes(p.main_position) &&
        p.is_verified &&
        (p.is_public !== false) &&
        (playerRatings[p.id] || 0) > 0
      )
      // Sort by rating descending, then by is_top_talent, then by goals
      candidates.sort((a, b) => {
        const ratA = playerRatings[a.id] || 0
        const ratB = playerRatings[b.id] || 0
        if (ratB !== ratA) return ratB - ratA
        if (a.is_top_talent && !b.is_top_talent) return -1
        if (!a.is_top_talent && b.is_top_talent) return 1
        return (b.goals || 0) - (a.goals || 0)
      })
      return { ...slot, player: candidates[0] || null }
    })
  }, [players, playerRatings])

  // Top Talents: admin-set OR rating >= 8.5
  const topTalents = useMemo(() => {
    const eligiblePlayers = players.filter(p => {
      // Must be admin-set top talent OR have average rating >= 8.5
      const hasRating = (playerRatings[p.id] || 0) >= 8.5
      if (!p.is_top_talent && !hasRating) return false
      // Must be verified
      if (!p.is_verified) return false
      // Club visibility checks
      if (p.owner_club_id && !visibleClubIds.has(p.owner_club_id)) return false
      if (p.club && hiddenClubNames.has(p.club.trim().toLowerCase())) return false
      return true
    })
    const hasExplicitlyPublicPlayers = eligiblePlayers.some((player) => player.is_public !== false)
    return eligiblePlayers.filter((player) => hasExplicitlyPublicPlayers ? player.is_public !== false : true)
  }, [players, visibleClubIds, hiddenClubNames, playerRatings])

  // Apply sorting to search results
  const sortedTalents = useMemo(() => {
    const sorted = [...searchResults]

    sorted.sort((a, b) => {
      let compareValue = 0

      if (sortField === 'name') {
        compareValue = a.name.localeCompare(b.name)
      } else if (sortField === 'age') {
        compareValue = a.age - b.age
      } else if (sortField === 'goals') {
        compareValue = (b.goals || 0) - (a.goals || 0)
      } else if (sortField === 'assists') {
        compareValue = (b.assists || 0) - (a.assists || 0)
      } else if (sortField === 'passes') {
        compareValue = (b.kp || 0) - (a.kp || 0)
      } else if (sortField === 'potential') {
        compareValue = (b.potential || 0) - (a.potential || 0)
      } else if (sortField === 'position') {
        const positionOrder = ['GK', 'CB', 'LB', 'RB', 'CDM', 'CM', 'CAM', 'RWF', 'LWF', 'SS', 'CF']
        compareValue = positionOrder.indexOf(a.main_position) - positionOrder.indexOf(b.main_position)
      } else if (sortField === 'recent') {
        compareValue = new Date(b.created_at || '').getTime() - new Date(a.created_at || '').getTime()
      } else if (sortField === 'rating') {
        compareValue = (playerRatings[b.id] || 0) - (playerRatings[a.id] || 0)
      }

      return sortOrder === 'asc' ? compareValue : -compareValue
    })

    return sorted
  }, [searchResults, sortField, sortOrder, playerRatings])

  // Carousel scroll
  const checkCarouselScroll = () => {
    if (!carouselRef.current) return
    const { scrollLeft, scrollWidth, clientWidth } = carouselRef.current
    setCanScrollLeft(scrollLeft > 5)
    setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 5)
  }

  useEffect(() => {
    checkCarouselScroll()
    const el = carouselRef.current
    if (el) {
      el.addEventListener('scroll', checkCarouselScroll)
      return () => el.removeEventListener('scroll', checkCarouselScroll)
    }
  }, [featuredPlayers])

  const scrollCarousel = (direction: 'left' | 'right') => {
    if (!carouselRef.current) return
    const scrollAmount = 340
    carouselRef.current.scrollBy({
      left: direction === 'left' ? -scrollAmount : scrollAmount,
      behavior: 'smooth',
    })
  }

  if (selectedPlayer) {
    const player = players.find(p => p.id === selectedPlayer)
    if (player) {
      return (
        <div className="max-w-7xl mx-auto px-4 py-8">
          <PlayerDetail
            player={player}
            onBack={() => setSelectedPlayer(null)}
            canEdit={false}
          />
        </div>
      )
    }
  }

  return (
    <div className="min-h-screen surface-bg">
      {/* Hero Section */}
      <div className="bg-[#0a151f] border-b border-white/5 py-20 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-1/2 h-full bg-[#7cff01]/5 blur-[120px] rounded-full -translate-y-1/2 translate-x-1/2" />

        <div className="max-w-7xl mx-auto px-4 relative z-10">
          <div className="max-w-3xl space-y-6">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#7cff01]/10 border border-[#7cff01]/20 text-[#7cff01] text-[10px] uppercase font-bold tracking-widest">
              <Award size={12} />
              Curated Excellence
            </div>
            <h1 className="text-5xl md:text-7xl font-black text-white leading-none">
              Scouta <span className="text-[#7cff01]">Top Talents</span>
            </h1>
            <div className="space-y-4 text-xl text-slate-300 leading-relaxed max-w-2xl">
              <p>
                Scouta Top Talents features players rated 8.5+ by verified scouts and clubs, plus admin-curated picks.
              </p>
              <p>
                Every rating is from a registered scout or club — real assessments from real professionals.
              </p>
            </div>

            <div className="pt-8 flex flex-col sm:flex-row gap-6 items-start sm:items-center">
              {isGuest && (
                <button
                  onClick={onLoginClick}
                  className="bg-[#7cff01] hover:bg-[#8eff1a] text-black px-6 py-3 rounded-full flex items-center gap-2 font-black transition-all active:scale-95 shadow-lg shadow-[#7cff01]/10 text-xs uppercase tracking-widest"
                >
                  <LogIn size={18} /> Sign In to View More
                </button>
              )}

              <div>
                <p className="text-[#888] text-[10px] uppercase font-bold tracking-[0.2em] mb-3">Want to feature a player?</p>
                <div className="flex gap-4">
                  <a href="mailto:support@scouta.ng" className="flex items-center gap-2 text-white hover:text-[#7cff01] transition-colors font-bold">
                    <Mail size={18} /> Email Us
                  </a>
                  <a href="https://x.com/scoutang" target="_blank" rel="noreferrer" className="flex items-center gap-2 text-white hover:text-[#7cff01] transition-colors font-bold">
                    <Twitter size={18} /> Twitter (X)
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Featured Players Carousel */}
        {featuredPlayers.some(f => f.player) && (
          <div className="mb-12">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-2xl font-black text-white">Featured Players</h2>
                <p className="text-sm text-gray-400 mt-1">Top-rated players by position</p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => scrollCarousel('left')}
                  disabled={!canScrollLeft}
                  className="w-10 h-10 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center text-gray-400 hover:text-white hover:bg-white/10 transition disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  <ChevronLeft size={18} />
                </button>
                <button
                  onClick={() => scrollCarousel('right')}
                  disabled={!canScrollRight}
                  className="w-10 h-10 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center text-gray-400 hover:text-white hover:bg-white/10 transition disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  <ChevronRight size={18} />
                </button>
              </div>
            </div>

            <div
              ref={carouselRef}
              className="flex gap-5 overflow-x-auto hide-scrollbar snap-x snap-mandatory scroll-smooth"
            >
              {featuredPlayers.map((slot, i) => (
                <div
                  key={i}
                  className="snap-start shrink-0 w-[320px]"
                >
                  {slot.player ? (
                    <FeaturedCard
                      player={slot.player}
                      slot={slot}
                      rating={playerRatings[slot.player.id] || 0}
                      onClick={() => setSelectedPlayer(slot.player!.id)}
                    />
                  ) : (
                    <div className="h-[400px] bg-[#161b22] border border-gray-800 rounded-3xl flex flex-col items-center justify-center opacity-50">
                      <div className={`w-14 h-14 rounded-2xl ${slot.bg} flex items-center justify-center mb-3`}>
                        <span className={slot.color}>{slot.icon}</span>
                      </div>
                      <p className="text-xs font-bold text-gray-500 uppercase tracking-widest">{slot.label}</p>
                      <p className="text-[10px] text-gray-600 mt-1">No rated players yet</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Search and Controls Section */}
        <div className="space-y-3 mb-8">
          {/* Search Bar */}
          <div className="surface-glass p-4 backdrop-blur-xl border border-white/5 rounded-3xl shadow-tactical">
            <PlayerSearch
              players={topTalents}
              onSearch={setSearchResults}
              placeholder="Search top talents..."
            />
          </div>

          {/* Sort Toolbar */}
          <div className="flex items-center gap-2 overflow-x-auto whitespace-nowrap hide-scrollbar">
            <button
              onClick={() => setShowSortMenu(!showSortMenu)}
              className={`shrink-0 flex items-center gap-2 px-4 py-2.5 rounded-2xl text-xs font-black uppercase tracking-widest border transition-all ${
                showSortMenu
                  ? 'bg-[#7cff01]/10 border-[#7cff01]/30 text-[#7cff01]'
                  : 'bg-white/5 border-white/10 text-gray-400 hover:text-white'
              }`}
            >
              <ArrowUpDown size={14} />
              {sortField === 'name' ? 'Name' : sortField === 'age' ? 'Age' : sortField === 'goals' ? 'Goals' : sortField === 'assists' ? 'Assists' : sortField === 'passes' ? 'Passes' : sortField === 'potential' ? 'Potential' : sortField === 'position' ? 'Position' : sortField === 'rating' ? 'Rating' : 'Recent'}
              <ChevronDown size={12} className={`transition-transform ${showSortMenu ? 'rotate-180' : ''}`} />
            </button>

            <button
              onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
              className="shrink-0 px-4 py-2.5 rounded-2xl text-xs font-black uppercase tracking-widest border bg-white/5 border-white/10 text-gray-400 hover:text-white transition-all"
            >
              {sortOrder === 'asc' ? '↑ Asc' : '↓ Desc'}
            </button>

            <div className="shrink-0 ml-auto px-4 py-2.5 rounded-2xl text-[10px] font-black uppercase tracking-widest border bg-[#021a1a] border-white/5 text-gray-400">
              {sortedTalents.length} {sortedTalents.length === 1 ? 'talent' : 'talents'}
            </div>
          </div>

          {/* Sort Dropdown Panel */}
          {showSortMenu && (
            <div className="surface-glass border border-white/10 rounded-3xl p-5 shadow-tactical animate-in slide-in-from-top-2 fade-in duration-200">
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
                {[
                  { id: 'rating', label: 'Rating' },
                  { id: 'age', label: 'Age' },
                  { id: 'goals', label: 'Goals' },
                  { id: 'assists', label: 'Assists' },
                  { id: 'passes', label: 'Passes' },
                  { id: 'potential', label: 'Potential' },
                  { id: 'position', label: 'Position' },
                  { id: 'name', label: 'Name' },
                  { id: 'recent', label: 'Recent' }
                ].map((option) => (
                  <button
                    key={option.id}
                    onClick={() => { setSortField(option.id as SortField); setShowSortMenu(false) }}
                    className={`px-4 py-2.5 rounded-xl text-xs font-black uppercase tracking-widest transition-all border ${
                      sortField === option.id
                        ? 'bg-[#7cff01]/10 border-[#7cff01]/30 text-[#7cff01]'
                        : 'bg-white/5 border-white/5 text-gray-400 hover:text-white hover:bg-white/10'
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {sortedTalents.length === 0 ? (
          <div className="text-center py-20 space-y-4">
            <div className="w-20 h-20 bg-gray-900 rounded-full flex items-center justify-center mx-auto text-gray-700">
               <Award size={40} />
            </div>
            <p className="text-gray-500 font-bold uppercase tracking-widest">Curating standout profiles...</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2 sm:gap-5 px-1 sm:px-0">
            {sortedTalents.map(player => (
              <PlayerCard
                key={player.id}
                player={player}
                onViewProfile={(id) => setSelectedPlayer(id)}
                rating={playerRatings[player.id]}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

// Featured Card Component
function FeaturedCard({ player, slot, rating, onClick }: {
  player: Player
  slot: FeaturedSlot
  rating: number
  onClick: () => void
}) {
  return (
    <button
      onClick={onClick}
      className="h-[400px] bg-[#161b22] border border-gray-800 rounded-3xl overflow-hidden text-left group hover:border-white/20 transition-all hover:scale-[1.02] active:scale-[0.98]"
    >
      {/* Header */}
      <div className={`relative h-48 ${slot.bg} overflow-hidden`}>
        {player.photo_url ? (
          <img
            src={player.photo_url}
            alt={player.name}
            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <span className={`text-6xl font-black ${slot.color} opacity-20`}>
              {player.name?.charAt(0)}
            </span>
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />

        {/* Rating Badge */}
        <div className="absolute top-4 right-4 px-3 py-1.5 bg-black/60 backdrop-blur-sm rounded-full flex items-center gap-1.5">
          <Star size={12} className="text-yellow-400 fill-yellow-400" />
          <span className="text-sm font-black text-white">{rating.toFixed(1)}</span>
        </div>

        {/* Position Badge */}
        <div className="absolute top-4 left-4 px-3 py-1.5 bg-black/60 backdrop-blur-sm rounded-full">
          <span className="text-[10px] font-black text-white uppercase tracking-widest">{player.main_position}</span>
        </div>
      </div>

      {/* Info */}
      <div className="p-5 space-y-3">
        <div>
          <div className="flex items-center gap-2">
            <h3 className="text-lg font-black text-white truncate">{player.name}</h3>
            {player.is_top_talent && (
              <Award size={14} className="text-yellow-400 shrink-0" />
            )}
          </div>
          <p className="text-xs text-gray-400 mt-0.5">{player.club || 'Free Agent'} • {player.age} yrs</p>
        </div>

        <p className={`text-[10px] font-black uppercase tracking-[0.2em] ${slot.color}`}>
          {slot.label}
        </p>

        <div className="flex items-center gap-4 text-[10px] text-gray-500">
          <span>{player.goals || 0} goals</span>
          <span>{player.assists || 0} assists</span>
          <span>{player.apps || 0} apps</span>
        </div>
      </div>
    </button>
  )
}
